import { AlertCircle, Loader2, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { notify } from "../../lib/notify";
import { Service } from "../../types";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { EnvVarEditor } from "./env-vars";

// ── EnvVarsTab ────────────────────────────────────────────────────────────────
// API-connected tab that wraps EnvVarEditor with load/save and a pending-deploy
// banner. EnvVarEditor handles all local editing state; this component owns only
// the API lifecycle.

interface EnvVarsTabProps {
  service: Service;
  /** ObjectID from ?environmentId= URL param. Passed as ?environment= to the
   *  backend. Falls back to "development" when absent. */
  environmentId?: string;
  onServiceUpdate?: () => void;
}

export function EnvVarsTab({
  service,
  environmentId,
  onServiceUpdate,
}: EnvVarsTabProps) {
  const environment = environmentId ?? "development";

  // savedVars — what the backend holds. currentVars — editor's live state.
  const [savedVars, setSavedVars] = useState<Record<string, string>>({});
  const [currentVars, setCurrentVars] = useState<Record<string, string>>({});
  // Incrementing this key remounts EnvVarEditor, resetting its internal state.
  const [editorKey, setEditorKey] = useState(0);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [redeploying, setRedeploying] = useState(false);

  // ── Load ──────────────────────────────────────────────────────────────────

  const loadEnvVars = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.env.get(service.id, environment);
      const vars = data.envVars ?? {};
      setSavedVars(vars);
      setCurrentVars(vars);
      setEditorKey((k) => k + 1);
    } catch {
      notify.error("Failed to load environment variables");
    } finally {
      setLoading(false);
    }
  }, [service.id, environment]);

  useEffect(() => {
    loadEnvVars();
  }, [loadEnvVars]);

  // ── Dirty check ───────────────────────────────────────────────────────────

  const isDirty =
    JSON.stringify(
      Object.entries(currentVars).sort(([a], [b]) => a.localeCompare(b))
    ) !==
    JSON.stringify(
      Object.entries(savedVars).sort(([a], [b]) => a.localeCompare(b))
    );

  const changeCount = (() => {
    const changed = Object.keys(currentVars).filter(
      (k) => currentVars[k] !== savedVars[k]
    ).length;
    const added = Object.keys(currentVars).filter((k) => !(k in savedVars)).length;
    const deleted = Object.keys(savedVars).filter(
      (k) => !(k in currentVars)
    ).length;
    return changed - added + added + deleted;
  })();

  const pendingDeploy =
    service.envVarsPendingDeploy && service.status === "running";

  // ── Save ──────────────────────────────────────────────────────────────────

  async function handleSave() {
    setSaving(true);
    try {
      // Build a merge payload: current vars set as-is, deleted keys sent as "".
      const payload: Record<string, string> = { ...currentVars };
      for (const k of Object.keys(savedVars)) {
        if (!(k in currentVars)) payload[k] = "";
      }

      const data = await api.env.update(service.id, payload, environment);
      const next = data.envVars ?? {};
      setSavedVars(next);
      setCurrentVars(next);
      setEditorKey((k) => k + 1);
      notify.success("Environment variables saved", {
        description: "Redeploy to apply the new values.",
      });
      onServiceUpdate?.();
    } catch (err: any) {
      notify.error("Failed to save", {
        description: err?.message ?? "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  }

  // ── Redeploy ──────────────────────────────────────────────────────────────

  async function handleRedeploy() {
    setRedeploying(true);
    try {
      await api.services.redeploy(service.id, "env-change");
      notify.success("Redeploy started", {
        description:
          "Your service is rebuilding with the new environment variables.",
      });
      onServiceUpdate?.();
    } catch (err: any) {
      notify.error("Failed to redeploy", {
        description: err?.message ?? "Please try again.",
      });
    } finally {
      setRedeploying(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4 pt-4">
      {/* Pending deploy banner */}
      {pendingDeploy && (
        <div className="flex items-center justify-between rounded-lg bg-warning-bg px-4 py-3 text-sm">
          <div className="flex items-center gap-2 text-warning-fg">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>
              Environment variables changed since last deploy — your service is
              running with the old values.
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="ml-4 shrink-0 text-warning-fg hover:bg-warning-bg"
            disabled={redeploying}
            onClick={handleRedeploy}
          >
            {redeploying && (
              <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
            )}
            Redeploy now
          </Button>
        </div>
      )}

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Environment Variables</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Changes take effect after a redeploy. Values are encrypted at
                rest.
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              <EnvVarEditor
                key={editorKey}
                defaultValue={savedVars}
                onChange={setCurrentVars}
              />

              {/* Save bar */}
              {isDirty && (
                <div className="flex items-center justify-between px-1">
                  <p className="text-xs text-muted-foreground">
                    <span className="text-foreground font-medium">
                      {changeCount}
                    </span>{" "}
                    unsaved change{changeCount !== 1 ? "s" : ""}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadEnvVars}
                      disabled={saving}
                    >
                      Discard
                    </Button>
                    <Button
                      size="sm"
                      className="gap-1.5"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Save className="h-3.5 w-3.5" />
                      )}
                      Save
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground px-1">
        Keys matching common secret patterns (
        <span className="font-mono">*_KEY</span>,{" "}
        <span className="font-mono">*_TOKEN</span>,{" "}
        <span className="font-mono">PASSWORD</span>, etc.) are masked by
        default.
      </p>
    </div>
  );
}
